import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
export function Combobox({ options, value, onChange, placeholder = '— Select —', noMatchesLabel = 'No matches', width, id, name, invalid, disabled, }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const selected = options.find((o) => o.value === value);
    const filtered = query.trim()
        ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
        : options;
    // What the user sees in the input: the chosen label when the box is at rest,
    // the typed query while the dropdown is open. This lets them type to filter
    // without first having to clear the existing selection.
    const displayValue = open ? query : (selected?.label ?? '');
    useEffect(() => {
        if (!open)
            return;
        function onMouseDown(e) {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
                setQuery('');
                setActiveIndex(-1);
            }
        }
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [open]);
    // Keep the highlighted option scrolled into view as the user arrows down a
    // long list — important for months across multiple years.
    useEffect(() => {
        if (!open || activeIndex < 0)
            return;
        const list = rootRef.current?.querySelector('.kb-combobox__list');
        const active = list?.children[activeIndex];
        if (active && list) {
            const top = active.offsetTop;
            const bottom = top + active.offsetHeight;
            if (top < list.scrollTop)
                list.scrollTop = top;
            else if (bottom > list.scrollTop + list.clientHeight) {
                list.scrollTop = bottom - list.clientHeight;
            }
        }
    }, [open, activeIndex]);
    function commit(opt) {
        onChange(opt.value);
        setOpen(false);
        setQuery('');
        setActiveIndex(-1);
        inputRef.current?.blur();
    }
    function onKey(e) {
        if (disabled)
            return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open)
                setOpen(true);
            setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        }
        else if (e.key === 'Enter') {
            if (activeIndex >= 0 && filtered[activeIndex]) {
                e.preventDefault();
                commit(filtered[activeIndex]);
            }
        }
        else if (e.key === 'Escape') {
            setOpen(false);
            setQuery('');
            setActiveIndex(-1);
        }
    }
    const inputClasses = ['kb-input', 'kb-combobox__input', invalid && 'kb-input--invalid']
        .filter(Boolean)
        .join(' ');
    return (_jsxs("div", { ref: rootRef, className: "kb-combobox", role: "combobox", "aria-haspopup": "listbox", "aria-expanded": open, style: width !== undefined ? { width } : undefined, children: [_jsx("input", { ref: inputRef, id: id, name: name, type: "text", className: inputClasses, value: displayValue, placeholder: placeholder, disabled: disabled, onFocus: () => {
                    if (disabled)
                        return;
                    setOpen(true);
                    setQuery('');
                    setActiveIndex(-1);
                }, onChange: (e) => {
                    setQuery(e.currentTarget.value);
                    if (!open)
                        setOpen(true);
                    setActiveIndex(0);
                }, onKeyDown: onKey, autoComplete: "off", spellCheck: false }), open && (_jsx("div", { className: "kb-combobox__list", role: "listbox", children: filtered.length === 0 ? (_jsx("div", { className: "kb-combobox__empty", children: noMatchesLabel })) : (filtered.map((o, i) => {
                    const isActive = i === activeIndex;
                    const isSelected = o.value === value;
                    const classes = [
                        'kb-combobox__option',
                        isActive && 'kb-combobox__option--active',
                        isSelected && 'kb-combobox__option--selected',
                    ]
                        .filter(Boolean)
                        .join(' ');
                    return (_jsx("div", { role: "option", "aria-selected": isSelected, className: classes, 
                        // mousedown (not click) so the input doesn't blur first
                        // and dismiss the list before our handler fires.
                        onMouseDown: (e) => {
                            e.preventDefault();
                            commit(o);
                        }, onMouseEnter: () => setActiveIndex(i), children: o.label }, o.value));
                })) }))] }));
}
//# sourceMappingURL=Combobox.js.map